import { from, throwError } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { fetch$ } from '@onviobr/fetch';
import { BaseHttpClientV1 } from '@onviobr/helpers';
/**
 * Implementation of {@link @onviobr/helpers#BaseHttpClientV1} that adds authentication to all calls
 * @public
 */
export class SecureHttp extends BaseHttpClientV1 {
    /**
     * Implementation of {@link @onviobr/helpers#BaseHttpClientV} that adds authentication to all calls
     * @param session - The {@link SessionManager} to use to get session information
     * @param cacheKey - Global key used to cache the requests
     */
    constructor(session, cacheKey) {
        super(fetch$, cacheKey);
        this.session = session;
    }
    /**
     * Performs an authenticated GET request to a path in a service
     * @remarks The `service` url is resolved using the Routing Table
     * @param service - Service name
     * @param input - Request input
     * @param init - Request init information
     * @param cached - Whether the request should be cached or not
     * @returns The response body as JSON
     */
    get(service, input, init, cached) {
        return this.getServiceURL(service).pipe(map(url => this.injectBaseUrl(url, input, init)), switchMap(args => this.getRaw(...args, cached)));
    }
    /**
     * Performs an authenticated POST request to a path in a service
     * @remarks The `service` url is resolved using the Routing Table
     * @param service - Service name
     * @param input - Request input
     * @param init - Request init information
     * @returns The response body as JSON
     */
    post(service, input, init) {
        return this.getServiceURL(service).pipe(map(url => this.injectBaseUrl(url, input, init)), switchMap(args => this.postRaw(...args)));
    }
    /**
     * Performs an authenticated PUT request to a path in a service
     * @remarks The `service` url is resolved using the Routing Table
     * @param service - Service name
     * @param input - Request input
     * @param init - Request init information
     * @returns The response body as JSON
     */
    put(service, input, init) {
        return this.getServiceURL(service).pipe(map(url => this.injectBaseUrl(url, input, init)), switchMap(args => this.putRaw(...args)));
    }
    /**
     * Performs an authenticated DELETE request to a path in a service
     * @remarks The `service` url is resolved using the Routing Table
     * @param service - Service name
     * @param input - Request input
     * @param init - Request init information
     * @returns Nothing
     */
    delete(service, input, init) {
        return this.getServiceURL(service).pipe(map(url => this.injectBaseUrl(url, input, init)), switchMap(args => this.deleteRaw(...args)));
    }
    /**
     * Internal function used to get the session token in all requests
     * @param input - Request input
     * @param init - Request init information
     * @returns The request input with the session token injected
     */
    source(input, init) {
        return this.session.authenticated.pipe(switchMap(authenticated => {
            if (!authenticated) {
                return throwError(new Error('Not authenticated'));
            }
            return this.session.currentUDSLongToken;
        }), 
        // This ensure that any change on the session does not re-kick any request
        take(1), map(uds => this.injectHeader('Authorization', `UDSLongToken ${uds}`, input, init)));
    }
    getServiceURL(service) {
        return from(this.session.getVerticalRoute(service));
    }
}
